/**
 * 列表逐条入场。
 *
 * 一次刷出 30 行和逐条错开冒出来，观感是完全不同的。
 * 但要克制：总时长必须压住（stagger × 条数），否则最后一条要等一秒多，
 * 那就不是"丝滑"而是"卡"。
 */

import { useGSAP } from '@gsap/react';
import { useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

export interface StaggerInOptions {
	selector?: string;
	y?: number;
	/** 单条间隔，会根据条数自动压缩 */
	stagger?: number;
	/** 整段动画的总时长上限 */
	maxTotal?: number;
}

export function useStaggerIn<T extends HTMLElement = HTMLDivElement>(
	deps: unknown[],
	options: StaggerInOptions = {},
): RefObject<T> {
	const scope = useRef<T | null>(null);
	const {
		selector = '[data-stagger]',
		y = 12,
		stagger = MOTION.stagger,
		maxTotal = 0.42,
	} = options;

	useGSAP(
		() => {
			if (!scope.current || prefersReducedMotion()) return;

			const targets = scope.current.querySelectorAll(selector);
			if (targets.length === 0) return;

			// 条数多的时候压缩间隔，保证整段不超过 maxTotal
			const step = Math.min(stagger, maxTotal / Math.max(targets.length, 1));

			gsap.fromTo(
				targets,
				{ autoAlpha: 0, y },
				{
					autoAlpha: 1,
					y: 0,
					duration: MOTION.enter,
					stagger: step,
					ease: MOTION.ease.expo,
					overwrite: 'auto',
					clearProps: 'transform',
				},
			);
		},
		{ scope, dependencies: deps, revertOnUpdate: true },
	);

	return scope as RefObject<T>;
}
